import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "./contexts/AuthContext";
import { CartProvider } from "./contexts/CartContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { SettingsProvider, useSettings } from "./contexts/SettingsContext";
import { I18nProvider } from "./contexts/I18nContext";
import Header from "./components/Header";
import Footer from "./components/Footer";
import CartDrawer from "./components/CartDrawer";
import ChatWidget from "./components/ChatWidget";
import AuthCallback from "./components/AuthCallback";
import GoogleAuthCallback from "./components/GoogleAuthCallback";
import ProtectedRoute from "./components/ProtectedRoute";
import CookieBanner from "./components/CookieBanner";
import WelcomePopup from "./components/WelcomePopup";

import Home from "./pages/Home";
import Shop from "./pages/Shop";
import ProductPage from "./pages/ProductPage";
import Packages from "./pages/Packages";
import PackageDetail from "./pages/PackageDetail";
import Services from "./pages/Services";
import Booking from "./pages/Booking";
import Portfolio from "./pages/Portfolio";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Account from "./pages/Account";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ResetPassword from "./pages/ResetPassword";
import Unsubscribe from "./pages/Unsubscribe";
import StaticPage from "./pages/StaticPage";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminCategories from "./pages/admin/AdminCategories";
import AdminPackages from "./pages/admin/AdminPackages";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminDiscounts from "./pages/admin/AdminDiscounts";
import AdminBanners from "./pages/admin/AdminBanners";
import AdminPromotions from "./pages/admin/AdminPromotions";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminAppointments from "./pages/admin/AdminAppointments";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminTheme from "./pages/admin/AdminTheme";
import AdminNewsletter from "./pages/admin/AdminNewsletter";
import AdminBlog from "./pages/admin/AdminBlog";
import AdminPortfolio from "./pages/admin/AdminPortfolio";
import AdminReviews from "./pages/admin/AdminReviews";
import AdminContactMessages from "./pages/admin/AdminContactMessages";
import AdminAgenda from "./pages/admin/AdminAgenda";
import AdminTickets from "./pages/admin/AdminTickets";
import AdminChat from "./pages/admin/AdminChat";
import AdminInventory from "./pages/admin/AdminInventory";
import AdminClients from "./pages/admin/AdminClients";
import AdminClientDossier from "./pages/admin/AdminClientDossier";
import AdminServicesEdit from "./pages/admin/AdminServicesEdit";
import AdminImportExport from "./pages/admin/AdminImportExport";
import AdminAuditLog from "./pages/admin/AdminAuditLog";
import AdminInvoices from "./pages/admin/AdminInvoices";
import AdminHealth from "./pages/admin/AdminHealth";
import AdminReturns from "./pages/admin/AdminReturns";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminSuppliers from "./pages/admin/AdminSuppliers";
import AdminBulkOps from "./pages/admin/AdminBulkOps";
import AdminLoyalty from "./pages/admin/AdminLoyalty";
import AdminAccounting from "./pages/admin/AdminAccounting";

function GA() {
    const { settings } = useSettings();
    React.useEffect(() => {
        if (settings.google_analytics_id && !window.__ga_loaded) {
            window.__ga_loaded = true;
            const s = document.createElement("script");
            s.async = true;
            s.src = `https://www.googletagmanager.com/gtag/js?id=${settings.google_analytics_id}`;
            document.head.appendChild(s);
            window.dataLayer = window.dataLayer || [];
            window.gtag = function () { window.dataLayer.push(arguments); };
            window.gtag("js", new Date());
            window.gtag("config", settings.google_analytics_id);
        }
        if (settings.meta_pixel_id && !window.__fb_loaded) {
            window.__fb_loaded = true;
            !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
            window.fbq('init', settings.meta_pixel_id);
            window.fbq('track', 'PageView');
        }
    }, [settings]);
    return null;
}

function WhatsAppFab() {
    const { settings } = useSettings();
    const wa = (settings.whatsapp_number || "").replace(/\D/g, "");
    if (!wa) return null;
    return (
        <a href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer"
           className="fixed bottom-6 left-6 z-30 bg-[#25D366] text-white w-14 h-14 flex items-center justify-center shadow-lg hover:scale-105 transition-transform"
           data-testid="whatsapp-fab" title="Contactează-ne pe WhatsApp">
            <svg viewBox="0 0 24 24" className="w-7 h-7" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.67-.51-.173-.007-.371-.009-.57-.009-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        </a>
    );
}

function Layout() {
    const location = useLocation();
    const isAdmin = location.pathname.startsWith("/admin");
    return (
        <div className="min-h-screen flex flex-col">
            {!isAdmin && <Header />}
            <main className="flex-1">
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/magazin" element={<Shop />} />
                    <Route path="/magazin/:slug" element={<Shop />} />
                    <Route path="/produs/:slug" element={<ProductPage />} />
                    <Route path="/pachete" element={<Packages />} />
                    <Route path="/pachete/:slug" element={<PackageDetail />} />
                    <Route path="/servicii" element={<Services />} />
                    <Route path="/programare-instalare" element={<Booking />} />
                    <Route path="/portofoliu" element={<Portfolio />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                    <Route path="/despre" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/cos" element={<Cart />} />
                    <Route path="/checkout" element={<Checkout />} />
                    <Route path="/checkout/success" element={<CheckoutSuccess />} />
                    <Route path="/cont" element={<ProtectedRoute><Account /></ProtectedRoute>} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/reset-parola" element={<ResetPassword />} />
                    <Route path="/dezabonare" element={<Unsubscribe />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/inregistrare" element={<Register />} />
                    <Route path="/termeni" element={<StaticPage type="terms" />} />
                    <Route path="/gdpr" element={<StaticPage type="gdpr" />} />
                    <Route path="/retur" element={<StaticPage type="return" />} />
                    <Route path="/admin" element={<ProtectedRoute roles={["superadmin", "admin", "sales", "installer"]}><AdminLayout /></ProtectedRoute>}>
                        <Route index element={<AdminDashboard />} />
                        <Route path="products" element={<AdminProducts />} />
                        <Route path="categories" element={<AdminCategories />} />
                        <Route path="packages" element={<AdminPackages />} />
                        <Route path="users" element={<AdminUsers />} />
                        <Route path="discounts" element={<AdminDiscounts />} />
                        <Route path="banners" element={<AdminBanners />} />
                        <Route path="promotions" element={<AdminPromotions />} />
                        <Route path="orders" element={<AdminOrders />} />
                        <Route path="appointments" element={<AdminAppointments />} />
                        <Route path="settings" element={<AdminSettings />} />
                        <Route path="theme" element={<AdminTheme />} />
                        <Route path="newsletter" element={<AdminNewsletter />} />
                        <Route path="blog" element={<AdminBlog />} />
                        <Route path="portfolio" element={<AdminPortfolio />} />
                        <Route path="reviews" element={<AdminReviews />} />
                        <Route path="messages" element={<AdminContactMessages />} />
                        <Route path="agenda" element={<AdminAgenda />} />
                        <Route path="tickets" element={<AdminTickets />} />
                        <Route path="chat" element={<AdminChat />} />
                        <Route path="inventory" element={<AdminInventory />} />
                        <Route path="clients" element={<AdminClients />} />
                        <Route path="clients/dossier" element={<AdminClientDossier />} />
                        <Route path="services-edit" element={<AdminServicesEdit />} />
                        <Route path="import" element={<AdminImportExport />} />
                        <Route path="audit-log" element={<AdminAuditLog />} />
                        <Route path="invoices" element={<AdminInvoices />} />
                        <Route path="health" element={<AdminHealth />} />
                        <Route path="returns" element={<AdminReturns />} />
                        <Route path="analytics" element={<AdminAnalytics />} />
                        <Route path="suppliers" element={<AdminSuppliers />} />
                        <Route path="bulk-ops" element={<AdminBulkOps />} />
                        <Route path="loyalty" element={<AdminLoyalty />} />
                        <Route path="accounting" element={<AdminAccounting />} />
                    </Route>
                </Routes>
            </main>
            {!isAdmin && <Footer />}
            {!isAdmin && <CartDrawer />}
            {!isAdmin && <ChatWidget />}
            {!isAdmin && <WhatsAppFab />}
            {!isAdmin && <CookieBanner />}
            {!isAdmin && <WelcomePopup />}
            <GA />
        </div>
    );
}

function AppRouter() {
    const location = useLocation();

    // Capture ?ref=CODE and persist to localStorage for referral pre-fill
    React.useEffect(() => {
        const ref = new URLSearchParams(location.search).get("ref");
        if (ref) {
            localStorage.setItem("ref_code", ref.toUpperCase());
        }
    }, [location.search]);

    // Handle legacy Emergent Google auth callback (hash fragment)
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    // Handle native Google OAuth callback (?code=... on /cont)
    if (location.pathname === "/cont" && new URLSearchParams(location.search).get("code")) {
        return <GoogleAuthCallback />;
    }
    return <Layout />;
}

export default function App() {
    return (
        <BrowserRouter>
            <I18nProvider>
                <SettingsProvider>
                    <ThemeProvider>
                        <AuthProvider>
                            <CartProvider>
                                <Toaster position="top-right" richColors />
                                <AppRouter />
                            </CartProvider>
                        </AuthProvider>
                    </ThemeProvider>
                </SettingsProvider>
            </I18nProvider>
        </BrowserRouter>
    );
}
